import { Button, Modal } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import type { KitchenSizeChoice } from "../lib/kitchenSizePreference";
import { KitchenSizeSwitch } from "./KitchenStartOverlay";

export type KitchenSoundStatus = "locked" | "ready" | "muted" | "unsupported";

/**
 * «الشاشة» — THE BOARD'S DEVICE SHEET (plan §4.12).
 *
 * The header button opens it: this screen's size (the same choice the start
 * overlay offers) and the state of its new-order sound, with the one control
 * that changes it. Everything here is per DEVICE; nothing is sent to the
 * server. The view switch («حسب المحطة / حسب الطلب», D170) is NOT here: it sits
 * on the board's tools row, where the expediter switches mid-service.
 */
export function KitchenScreenSheet({
  isOpen,
  onClose,
  sizeChoice,
  onSizeChange,
  soundStatus,
  onEnableSound,
  onMuteSound,
}: {
  isOpen: boolean;
  onClose: () => void;
  sizeChoice: KitchenSizeChoice;
  onSizeChange: (next: KitchenSizeChoice) => void;
  /** `null` when the screen has no notifications runtime (the harness). */
  soundStatus: KitchenSoundStatus | null;
  onEnableSound: () => void;
  onMuteSound: () => void;
}) {
  const t = useTranslate();
  const status = soundStatus ?? "unsupported";
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title={t((m) => m.kitchen.screenTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="kitchen-screen-sheet"
      footerActions={
        <Button variant="outline" size="lg" data-testid="kitchen-screen-close" onClick={onClose}>
          {t((m) => m.common.close)}
        </Button>
      }
    >
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-2" data-ui="kitchen-screen-size">
          <h3 data-ui="kitchen-screen-heading">{t((m) => m.kitchen.sizeLabel)}</h3>
          <KitchenSizeSwitch value={sizeChoice} onChange={onSizeChange} />
        </section>
        <section className="flex flex-col gap-2" data-ui="kitchen-screen-sound">
          <h3 data-ui="kitchen-screen-heading">{t((m) => m.kitchen.soundLabel)}</h3>
          <p data-testid="kitchen-screen-sound-state" data-state={status}>
            {t((m) =>
              status === "ready"
                ? m.kitchen.soundOn
                : status === "muted"
                  ? m.kitchen.soundOff
                  : status === "locked"
                    ? m.kitchen.soundLocked
                    : m.kitchen.soundUnsupported,
            )}
          </p>
          {status === "unsupported" ? null : status === "muted" || status === "locked" ? (
            <Button variant="primary" size="lg" data-testid="kitchen-screen-sound-enable" onClick={onEnableSound}>
              {t((m) => m.kitchen.soundEnable)}
            </Button>
          ) : (
            <Button variant="outline" size="lg" data-testid="kitchen-screen-sound-mute" onClick={onMuteSound}>
              {t((m) => m.kitchen.soundMute)}
            </Button>
          )}
        </section>
      </div>
    </Modal>
  );
}
